import { useState, useEffect, useCallback, useRef } from 'react';

const HERO_SLIDES = [
  {
    src: "https://storage.readdy-site.link/project_files/39d93db1-4ba8-4195-8b83-3f891512ad57/c4a952e1-ea10-424d-a6cb-9c0177dbe888_007_2114.jpg?v=2645e2909aeaf3e6c51aed353d435662",
    alt: "施設写真1",
  },
  {
    src: "https://storage.readdy-site.link/project_files/39d93db1-4ba8-4195-8b83-3f891512ad57/2c02713c-d2e4-469c-a3cf-95a47b61a601_006_1176.jpg?v=533ead74b594d080fefa95d4ae14fbcb",
    alt: "施設写真2",
  },
  {
    src: "https://storage.readdy-site.link/project_files/39d93db1-4ba8-4195-8b83-3f891512ad57/fe6468d4-eb6b-44df-a417-9941fff881dd_006_1210.jpg?v=bfcc44f7b8c6228fedcc8173ad92a39d",
    alt: "施設写真3",
  },
  {
    src: "https://storage.readdy-site.link/project_files/39d93db1-4ba8-4195-8b83-3f891512ad57/82350bbb-daee-401e-a007-232bb932e0b0_006_1160.jpg?v=fded7f767b34ca8a32f117d9fadfa810",
    alt: "施設写真4",
  },
];

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % HERO_SLIDES.length);
  }, []);

  useEffect(() => {
    if (!isPaused) {
      timerRef.current = setInterval(next, 5000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, next]);

  return (
    <section
      className="relative w-full h-[65vh] min-h-[520px] flex items-center justify-center overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background Slideshow */}
      <div className="absolute inset-0">
        {HERO_SLIDES.map((slide, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              i === current ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.src}
              alt={slide.alt}
              className="w-full h-full object-cover object-center"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-[#1a1a3e]/55"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a3e]/20 via-transparent to-[#1a1a3e]/40"></div>
      </div>

      {/* Content - centered */}
      <div className="relative z-10 w-full px-6 lg:px-20 pt-16 flex flex-col items-center text-center">
        {/* SEO h1 - visually minimal but semantically primary */}
        <h1
          className="text-[#47d6d4] text-xs tracking-[0.35em] uppercase mb-2 font-medium"
          style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
        >
          Golph.club Suminoe — 大阪 住之江区 完全貸切シミュレーションゴルフ
        </h1>
        <p className="text-white/0 text-[0px] select-none h-0 overflow-hidden" aria-hidden="true">Simulation Golf — Osaka Suminoe</p>
        <div
          className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-serif-jp font-bold text-white leading-tight mb-5 tracking-tight"
          style={{ fontFamily: "'Noto Serif JP', serif" }}
          aria-hidden="true"
        >
          A New Golf Phase.
        </div>
        <p className="text-white/75 text-base sm:text-lg mb-10 leading-relaxed max-w-lg">
          測定で上達する、完全貸切のゴルフクラブ。
        </p>
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <a
            href="https://tdbs.me/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-[#47d6d4] text-white font-bold px-9 py-3.5 rounded-full text-sm hover:bg-[#3bc4c2] transition-all duration-200 whitespace-nowrap"
          >
            ビジター予約する
          </a>
          <a
            href="https://lin.ee/IX2y4CH"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center border-2 border-white text-white font-bold px-9 py-3.5 rounded-full text-sm hover:bg-white/15 transition-all duration-200 whitespace-nowrap"
          >
            無料体験を申し込む
          </a>
        </div>
      </div>

      {/* Dot Indicators */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex gap-2 items-center">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === current ? 'bg-white w-6' : 'bg-white/45 w-1.5'
            }`}
            aria-label={`スライド ${i + 1}`}
          />
        ))}
      </div>

      {/* Progress bar */}
      {!isPaused && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/15 z-20">
          <div
            key={current}
            className="h-full bg-white/50"
            style={{ animation: 'heroProgress 5000ms linear forwards' }}
          />
        </div>
      )}

      <style>{`
        @keyframes heroProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
}
